import LZString from 'lz-string';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import {
    type ContentKitRemoteInput,
    type ContentKitRemoteOutput,
    createContentKitRemote,
} from '@gitbook/contentkit-react';
import { createCache, createFastCacheKeyWithCollisionRisk } from '@gitbook/util-suspense-cache';

import { apiClient } from './api';

/**
 * We want to use a GET request whenever possible (to ensure it can be properly cached),
 * so we compress the request using LZString.
 * When the request will be too long for some browser, we fallback to a POST request.
 */
const GET_REQUEST_MAX_LENGTH = 2000;

const globalCache = createCache<ContentKitRemoteInput, ContentKitRemoteOutput>({
    getKey: (input) =>
        `${input.integrationId}-${input.componentId}-${createFastCacheKeyWithCollisionRisk(input)}`,
    getValue: async (input) => {
        const request: api.RequestRenderIntegrationUI = {
            componentId: input.componentId,
            action: input.action,
            props: input.props,
            state: input.state,
            context: input.context,
        };

        const serialized = JSON.stringify(request);
        const compressed = LZString.compressToEncodedURIComponent(serialized);

        const response = await (compressed.length < GET_REQUEST_MAX_LENGTH
            ? apiClient.integrations.renderIntegrationUiWithGet(input.integrationId, {
                  request: compressed,
              })
            : apiClient.integrations.renderIntegrationUiWithPost(input.integrationId, request));

        return response.data;
    },
});

/**
 * Create a ContentKit remote interface for rendering ContentKit UI blocks.
 */
export function useContentKitRemote() {
    return React.useMemo(() => {
        return createContentKitRemote((input) => {
            const state = globalCache.readCacheState({
                ...input,
            });

            if (state.result) {
                return state.result;
            }

            return globalCache.readAsync({
                ...input,
            });
        });
    }, []);
}
